import { useState, useCallback, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabase';
import {
  trackGenerationStarted,
  trackGenerationCompleted,
  trackGenerationFailed,
} from '@/lib/analytics';

export function useGenerate() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [streamedText, setStreamedText] = useState('');
  const [websiteId, setWebsiteId] = useState(null);
  // description -> url for the photographs the server resolved. The stream
  // only ever carried descriptions, so without this the preview shows a site
  // with empty image frames while the saved copy has the pictures.
  const [photos, setPhotos] = useState(null);
  const [error, setError] = useState(null);
  const [credits, setCredits] = useState(null);
  const [planLimit, setPlanLimit] = useState(null);
  const [promptEnhanced, setPromptEnhanced] = useState(false);
  // True when the run ended because the user pressed Stop.
  //
  // A stop and a run that died look identical from the outside: not generating
  // any more, no `complete` event, no error. The workspace needs to tell them
  // apart, because a stopped run IS saved — the server keeps whatever was
  // written and leaves it at 'completed' — so telling the user their work was
  // lost is both wrong and the opposite of what Stop promises.
  const [stopped, setStopped] = useState(false);

  // True while the server is clearing faults it found in the finished site.
  // The user is told what is happening, never what is wrong: a list of defects
  // is a bug report, and they asked for a website.
  const [repairing, setRepairing] = useState(false);

  // True once the server has given up on making an edit as a patch and is
  // writing the whole site out again. That takes minutes where a patch takes
  // seconds, and without being told, the user sees an edit that hangs and
  // then a site being rebuilt from the top.
  const [rewriting, setRewriting] = useState(false);

  // What could not be cleared. Almost always empty, and deliberately not shown
  // as a list — see GenerationView.
  const [issues, setIssues] = useState([]);

  // The site's icon when it is the owner's own logo, sent by the server before
  // the model writes a token (services/logoIcon.js). Preferred over the icon in
  // the stream, which the model draws and which would otherwise show first.
  const [siteIcon, setSiteIcon] = useState('');

  // True only once the server has confirmed the finished site is SAVED.
  //
  // The workspace used to infer success from "not generating any more, and we
  // have a website id". The id arrives with the `started` event, before a single
  // token — so every run that began and then died for any reason rendered
  // "✓ Website generated successfully!". A run whose stream was cut showed that
  // banner next to the error that killed it, and the user went on to publish a
  // row the server had never written a page to. Only `complete` means saved.
  const [completed, setCompleted] = useState(false);
  // Aborting this is what stops a run. Closing the request also closes the SSE
  // response, which the server watches for so it can cancel the model call —
  // stopping the UI alone would leave the tokens being billed.
  const abortRef = useRef(null);

  const resetState = useCallback(() => {
    setIsGenerating(false);
    setStreamedText('');
    setWebsiteId(null);
    setError(null);
    setPromptEnhanced(false);
    setCompleted(false);
    setStopped(false);
    setIssues([]);
    setRepairing(false);
    setRewriting(false);
    setSiteIcon('');
    // Note: We purposely do not reset credits so the user's optimistic local count persists
  }, []);

  /**
   * @param {string} prompt
   * @param {object} [options]
   * @param {string} [options.previousHtml]
   * @param {string} [options.previousCss]
   * @param {string} [options.previousJs]
   * @param {string} [options.existingWebsiteId]
   * @param {string} [options.base64Image]
   * @param {string} [options.imageMimeType]
   */
  const generate = async (prompt, options = {}) => {
    setPhotos(null);   // a new run starts with no photographs of its own
    resetState();
    setIsGenerating(true);
    trackGenerationStarted({ isRefinement: !!(options.existingWebsiteId), hasImage: !!(options.base64Image) });

    if (credits !== null && credits > 0) {
      setCredits((prev) => prev - 1);
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error('You must be logged in to generate a website.');
      }

      const apiUrl = import.meta.env.VITE_API_URL ?? '';

      const controller = new AbortController();
      abortRef.current = controller;

      const response = await fetch(`${apiUrl}/api/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ prompt, ...options }),
        signal: controller.signal,
      });

      if (!response.ok) {
        // Not every failure is JSON — a proxy or WAF can return bare text like
        // "Forbidden", and blindly calling .json() turned that into a useless
        // "Unexpected token 'F'" parse error. Read as text, then try JSON.
        const raw = await response.text().catch(() => '');
        let message;
        try {
          message = JSON.parse(raw)?.error;
        } catch {
          message = raw.trim().slice(0, 200);
        }
        if (!message) message = `Request failed (${response.status}).`;
        else if (response.status === 403) {
          message = `${message} — the request was blocked before it reached the generator. Please try again.`;
        }
        throw new Error(message);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');

      let done = false;
      let buffer = '';

      while (!done) {
        // Stop has to be decided here, not left to the abort.
        //
        // Aborting the controller is supposed to reject this read, and locally
        // it does. Through a buffering reverse proxy it does not: the bytes
        // already in flight keep arriving, the read keeps resolving, and the
        // run carries on painting after the user pressed Stop — which is
        // exactly what "the Stop button does nothing" looked like.
        //
        // Checking the signal ourselves makes stopping deterministic wherever
        // it runs, and cancelling the reader releases the stream rather than
        // leaving it draining in the background.
        if (controller.signal.aborted) {
          reader.cancel().catch(() => {});
          break;
        }

        const { value, done: readerDone } = await reader.read();
        done = readerDone;

        if (controller.signal.aborted) {
          reader.cancel().catch(() => {});
          break;
        }

        if (value) {
          buffer += decoder.decode(value, { stream: true });

          const lines = buffer.split('\n\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const dataStr = line.replace('data: ', '');

              let data;
              try {
                data = JSON.parse(dataStr);
              } catch (parseError) {
                console.error("SSE JSON parse error:", parseError, dataStr);
                continue;
              }

              if (data.type === 'chunk') {
                setStreamedText((prev) => prev + data.content);
              } else if (data.type === 'rewind') {
                // The model stalled or went wrong part-way. The server drops
                // the section it stopped in and writes it again, and the
                // preview keeps only what still stands.
                const keep = Math.max(0, Number(data.keep) || 0);
                setStreamedText((prev) => prev.slice(0, keep));
              } else if (data.type === 'icon') {
                if (typeof data.icon === 'string') setSiteIcon(data.icon);
              } else if (data.type === 'started') {
                // The server saved the row before streaming a single token.
                // Knowing the id this early is what lets the workspace put it in
                // the URL while the generation is still running.
                setWebsiteId(data.websiteId);
              } else if (data.type === 'enhanced') {
                setPromptEnhanced(true);
              } else if (data.type === 'photo') {
                // One photograph finished resolving while the site is still
                // being written. A new object each time so the render that
                // rebuilds the pages from the stream picks it up immediately —
                // the picture appears in place the moment it is ready.
                if (data.query && data.src) {
                  setPhotos((cur) => ({ ...(cur || {}), [data.query]: data.src }));
                }
              } else if (data.type === 'repairing') {
                setRepairing(true);
              } else if (data.type === 'rewriting') {
                setRewriting(true);
              } else if (data.type === 'complete') {
                setRepairing(false);
                setWebsiteId(data.websiteId);
                // Anything that resolved after the last chunk, plus a full copy
                // of the set so a reconnect mid-run still ends up complete.
                if (data.photos && Object.keys(data.photos).length) {
                  setPhotos((cur) => ({ ...(cur || {}), ...data.photos }));
                }
                trackGenerationCompleted({ websiteId: data.websiteId });
                if (Array.isArray(data.issues)) setIssues(data.issues);
                setCompleted(true);
                done = true;
              } else if (data.type === 'error') {
                throw new Error(data.message);
              }
            }
          }
        }
      }
    } catch (err) {
      // Stopping is a choice, not a failure. Reporting it as an error would put
      // a red banner in front of someone who did exactly what they meant to,
      // and would bury real failures in Sentry under cancellations.
      //
      // The credit is deliberately NOT refunded here: the server charged it and
      // real tokens were spent before the stop. Handing the local counter one
      // back would only disagree with the server on the next read.
      if (err.name === 'AbortError') {
        console.log('[GENERATE] stopped by the user');
        setStopped(true);
      } else {
        setError(err.message);
        trackGenerationFailed({ message: err.message });
        Sentry.captureException(err, { tags: { area: 'generation_client' } });

        if (credits !== null) {
          setCredits((prev) => prev + 1);
        }
      }
    } finally {
      abortRef.current = null;
      setIsGenerating(false);
    }
  };

  /**
   * Stop a run in progress. Whatever has already streamed stays on screen and
   * is saved server-side, so the user keeps the part they got.
   */
  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  return {
    generate,
    stop,
    isGenerating,
    streamedText,
    websiteId,
    photos,
    error,
    credits,
    setCredits,
    planLimit,
    setPlanLimit,
    resetState,
    promptEnhanced,
    completed,
    stopped,
    issues,
    repairing,
    rewriting,
    siteIcon,
  };
}
